import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useBranding } from '../context/BrandingContext';
import { useLocation } from 'react-router-dom';

const SESSION_KEY = 'shown_popups_session';
const EVER_KEY = 'shown_popups_ever';

const getSessionShown = () => {
  try { return new Set(JSON.parse(sessionStorage.getItem(SESSION_KEY) || '[]')); } catch { return new Set(); }
};
const getEverShown = () => {
  try { return new Set(JSON.parse(localStorage.getItem(EVER_KEY) || '[]')); } catch { return new Set(); }
};
const markSessionShown = (id) => {
  const set = getSessionShown(); set.add(String(id));
  sessionStorage.setItem(SESSION_KEY, JSON.stringify([...set]));
};
const markEverShown = (id) => {
  const set = getEverShown(); set.add(String(id));
  localStorage.setItem(EVER_KEY, JSON.stringify([...set]));
};

const PopupManager = () => {
  const { user } = useAuth();
  const { currentBranding } = useBranding();
  const [visiblePopups, setVisiblePopups] = useState([]);
  const scheduledRef = useRef(new Set()); // ids ya programados para mostrar
  const location = useLocation();

  useEffect(() => {
    if (!user) { setVisiblePopups([]); return; }

    const popups = currentBranding?.popups || [];
    const pathname = location.pathname;
    const sessionShown = getSessionShown();
    const everShown = getEverShown();

    console.log('🔔 PopupManager | pathname:', pathname);
    console.log('🔔 PopupManager | popups en currentBranding:', popups);
    console.log('🔔 PopupManager | currentBranding keys:', Object.keys(currentBranding || {}));

    // Reiniciar timers al cambiar de página o de popups
    scheduledRef.current = new Set();
    setVisiblePopups([]);

    const eligible = popups.filter(popup => {
      if (!popup.active) { console.log('🔔 Popup ignorado (inactivo):', popup.title); return false; }
      if (!Array.isArray(popup.showOnPages) || popup.showOnPages.length === 0) { console.log('🔔 Popup ignorado (sin páginas):', popup.title); return false; }

      const matchesPage = popup.showOnPages.some(
        p => pathname === p || pathname.startsWith(p === '/' ? '/?' : p + '/')
      );
      if (!matchesPage) { console.log('🔔 Popup ignorado (página no coincide):', popup.title, '| showOnPages:', popup.showOnPages, '| pathname:', pathname); return false; }

      const trigger = popup.trigger || 'always';
      if (trigger === 'once_session' && sessionShown.has(String(popup.id))) { console.log('🔔 Popup ignorado (ya mostrado en sesión):', popup.title); return false; }
      if (trigger === 'once_ever' && everShown.has(String(popup.id))) { console.log('🔔 Popup ignorado (ya mostrado siempre):', popup.title); return false; }

      console.log('✅ Popup elegible:', popup.title);
      return true;
    });

    eligible.forEach((popup, index) => {
      if (scheduledRef.current.has(popup.id)) return;
      scheduledRef.current.add(popup.id);

      const delayMs = ((popup.delay || 0) + index * 0.5) * 1000;
      setTimeout(() => {
        setVisiblePopups(prev => {
          if (prev.find(p => p.id === popup.id)) return prev;
          return [...prev, popup];
        });
      }, delayMs);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBranding?.popups, location.pathname, user]);

  const handleClose = (popup) => {
    const trigger = popup.trigger || 'always';
    if (trigger === 'once_session') markSessionShown(popup.id);
    if (trigger === 'once_ever') markEverShown(popup.id);
    setVisiblePopups(prev => prev.filter(p => p.id !== popup.id));
  };

  return (
    <div className="fixed inset-0 z-[999] pointer-events-none">
      {visiblePopups.map(popup => (
        <AnimatePresence key={popup.id}>
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 pointer-events-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-3xl p-6 shadow-2xl max-w-md w-full relative"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            >
              <button
                onClick={() => handleClose(popup)}
                className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {popup.imageUrl && (
                <img src={popup.imageUrl} alt={popup.title} className="w-full h-48 object-cover rounded-2xl mb-4" />
              )}

              <h3 className="text-2xl font-bold text-gray-900 mb-3">{popup.title}</h3>
              {popup.content && <p className="text-gray-700 mb-4">{popup.content}</p>}

              {popup.buttonText && popup.buttonLink && (
                <motion.a
                  href={popup.buttonLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {popup.buttonText}
                </motion.a>
              )}
            </motion.div>
          </motion.div>
        </AnimatePresence>
      ))}
    </div>
  );
};

export default PopupManager;